// components
import ToolbarActionMenu from './ToolbarActionMenu';
import { StyledToolbarContainer, StyledToolbarSection } from './Toolbar.styles';
import DateNavigation from 'components/shared/DateNavigation/DateNavigation';
import { ActionStrip } from './ActionStrip';

// enums
import { ToolbarFlowEnum, ToolbarPositionEnum } from '../ToolbarWrapper.enums';

// hooks
import {
  useIsVerticalToolbar,
  useToolbarPosition
} from '../ToolbarPosition.context';

// styles
import { DateNavigationContainer } from 'components/shared/Header/Header.styles';

// types
import type { ToolbarProps } from './Toolbar.types';

const Toolbar = ({
  actions,
  additionalActions,
  dateNavigation,
  setToolbarPosition,
  onMouseEnterZen,
  onMouseLeaveZen,
  zenOpen,
  planUnitSelector,
  toolbarFlow = ToolbarFlowEnum.GRID,
  component
}: ToolbarProps) => {
  const toolbarPosition = useToolbarPosition();
  const isVertical = useIsVerticalToolbar();

  // The tour id is the toolbar's own and defaultPeriod is its default; the rest is the date
  // navigation's own props and goes through untouched, so a screen needing one more of them needs no
  // change here.
  const {
    dataTourId,
    defaultPeriod = 'month',
    ...dateNavigationProps
  } = dateNavigation ?? {};

  const alignRightActions = actions?.filter(
    (action) => action.alignment === 'right'
  );
  const alignLeftActions = actions?.filter(
    (action) => action.alignment === 'left'
  );

  return (
    // The styled container is the edge: it needs plain booleans for its shouldForwardProp, so they
    // are derived here from the one enum and go no further.
    <StyledToolbarContainer
      isRightPositioned={toolbarPosition === ToolbarPositionEnum.RIGHT}
      isZenPositioned={toolbarPosition === ToolbarPositionEnum.ZEN}
      showDateNavigation={!!dateNavigation}
      zenOpen={zenOpen}
      onMouseEnter={onMouseEnterZen}
      onMouseLeave={onMouseLeaveZen}
      toolbarFlow={toolbarFlow}
    >
      {/* Exactly three children, one per grid area, so the date picker keeps the middle column even
          when components are injected either side of it. */}
      <StyledToolbarSection isVertical={isVertical}>
        <ActionStrip
          items={alignLeftActions}
          planUnitSelector={planUnitSelector}
        />

        {component?.beforeDatePicker}
      </StyledToolbarSection>

      {dateNavigation && (
        <DateNavigationContainer data-tourid={dataTourId}>
          {/* The toolbar-owned props go *after* the spread and must stay there: the type keeps them
              out of what a screen can pass, but that is only enforced for fresh object literals, so
              the ordering is what actually stops a screen overriding the toolbar's own layout. */}
          <DateNavigation
            {...dateNavigationProps}
            defaultPeriod={defaultPeriod}
            isToolbar
            isRightPositioned={isVertical}
          />
        </DateNavigationContainer>
      )}

      <StyledToolbarSection isVertical={isVertical}>
        {component?.afterDatePicker}

        <ActionStrip items={alignRightActions} justifyEnd>
          <ToolbarActionMenu
            additionalActions={additionalActions}
            isAdditionalActions
            setToolbarPosition={setToolbarPosition}
          />
        </ActionStrip>
      </StyledToolbarSection>
    </StyledToolbarContainer>
  );
};

export default Toolbar;
